import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, addDoc, getDocs, updateDoc, deleteDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { BrandGradient } from '../components/common/PFILogo';
import { formatProjectName } from '../utils/projectUtils';
import { formatDate } from '../utils/dateFormatters';
import { 
  ArrowLeftIcon,
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  LinkIcon,
  UserGroupIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

export default function TaskQuotesPage() {
  const { id, taskId } = useParams();
  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [quoteMessage, setQuoteMessage] = useState('');

  useEffect(() => {
    loadData();
  }, [id, taskId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectDoc = await getDoc(doc(db, 'projects', id));
      if (projectDoc.exists()) {
        setProject({ id: projectDoc.id, ...projectDoc.data() });
      }

      // Load task
      const taskDoc = await getDoc(doc(db, 'projects', id, 'tasks', taskId));
      if (taskDoc.exists()) {
        setTask({ id: taskDoc.id, ...taskDoc.data() });
      }

      // Load quotes from both old structure and new quoteRequests collection
      // First try the old structure
      const quotesSnapshot = await getDocs(collection(db, 'projects', id, 'tasks', taskId, 'quotes'));
      let quotesData = quotesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        _collection: 'oldQuotes' // Track which collection this came from
      }));
      
      // Also check the new quoteRequests collection
      const quoteRequestsQuery = query(
        collection(db, 'quoteRequests'),
        where('projectId', '==', id),
        where('taskId', '==', taskId)
      );
      const quoteRequestsSnapshot = await getDocs(quoteRequestsQuery);
      const quoteRequestsData = quoteRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Map fields to match expected structure
        supplierName: doc.data().supplierName,
        supplierEmail: doc.data().supplierEmail,
        status: doc.data().status || 'pending',
        invitedAt: doc.data().invitedAt || doc.data().createdAt,
        magicLink: doc.data().magicLink,
        _collection: 'quoteRequests' // Track which collection this came from
      }));
      
      // Combine both sources, removing duplicates by supplier ID
      const combinedQuotes = [...quotesData];
      quoteRequestsData.forEach(qr => {
        if (!combinedQuotes.find(q => q.supplierId === qr.supplierId)) {
          combinedQuotes.push(qr);
        }
      });
      
      setQuotes(combinedQuotes);

      // Check if task status needs updating based on quote statuses
      if (taskDoc.exists()) {
        const taskData = { id: taskDoc.id, ...taskDoc.data() };
        const submittedQuotes = combinedQuotes.filter(q => q.status === 'submitted');
        const acceptedQuotes = combinedQuotes.filter(q => q.status === 'accepted');
        
        let newTaskStatus = taskData.status;
        let shouldUpdate = false;

        // Update task status based on quote states
        if (acceptedQuotes.length > 0 && taskData.status !== 'quote_accepted') {
          newTaskStatus = 'quote_accepted';
          shouldUpdate = true;
        } else if (submittedQuotes.length > 0 && !['quotes_received', 'quote_accepted'].includes(taskData.status)) {
          newTaskStatus = 'quotes_received';
          shouldUpdate = true;
          
          // Create notification for quote received
          await addDoc(collection(db, 'notifications'), {
            type: 'quote_received',
            title: 'Quote Received',
            message: `${submittedQuotes.length} quote(s) received for task "${taskData.name}" in project "${project?.name || 'Unknown'}"`,
            projectId: id,
            taskId: taskId,
            read: false,
            createdAt: serverTimestamp(),
            recipientRole: 'admin'
          });
        }

        if (shouldUpdate) {
          await updateDoc(doc(db, 'projects', id, 'tasks', taskId), {
            status: newTaskStatus,
            statusUpdatedAt: serverTimestamp()
          });
          // Reload task data to reflect the update
          const updatedTaskDoc = await getDoc(doc(db, 'projects', id, 'tasks', taskId));
          if (updatedTaskDoc.exists()) {
            setTask({ id: updatedTaskDoc.id, ...updatedTaskDoc.data() });
          }
        }
      }

      // Load suppliers
      const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
      const suppliersData = suppliersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSuppliers(suppliersData);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMagicLink = (supplierId) => {
    const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
    return `${window.location.origin}/quote/${token}?project=${id}&task=${taskId}&supplier=${supplierId}`;
  };

  const handleInviteSuppliers = async () => {
    // Ensure project and task data is loaded
    if (!project || !task) {
      alert('⚠️ Project or task data not loaded. Please wait a moment and try again.');
      return;
    }

    try {
      console.log('Creating invitations with project:', project.name, 'and task:', task.name);
      
      // Collect photos for quote requests
      console.log('Collecting photos for quote requests...');
      let generalPhotos = [];
      let componentPhotos = [];
      let fabricPhotos = [];
      
      try {
        // Load general project photos
        const generalPhotosQuery = query(
          collection(db, 'projectPhotos'),
          where('projectId', '==', id),
          orderBy('uploadedAt', 'desc')
        );
        const generalPhotosSnapshot = await getDocs(generalPhotosQuery);
        generalPhotos = generalPhotosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'general'
        }));
        console.log(`Loaded ${generalPhotos.length} general photos`);
        
        // Load component-specific photos (if this task has a component)
        if (task.componentId) {
          const componentPhotosQuery = query(
            collection(db, 'componentPhotos'),
            where('projectId', '==', id),
            where('componentId', '==', task.componentId),
            orderBy('uploadedAt', 'desc')
          );
          const componentPhotosSnapshot = await getDocs(componentPhotosQuery);
          componentPhotos = componentPhotosSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'component',
            componentName: task.componentName || 'Component'
          }));
          console.log(`Loaded ${componentPhotos.length} component photos`);
        }
        
        // Load fabric images
        const fabricImagesSnapshot = await getDocs(collection(db, 'projects', id, 'fabricImages'));
        fabricPhotos = fabricImagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'fabric'
        }));
        console.log(`Loaded ${fabricPhotos.length} fabric photos`);
        
      } catch (error) {
        console.error('Error loading photos:', error);
        // Continue even if photo loading fails
      }
      
      for (const supplierId of selectedSuppliers) {
        const supplier = suppliers.find(s => s.id === supplierId);
        const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
        const magicLink = `${window.location.origin}/quote/${token}`;
        
        // Create quote request record
        const quoteRef = await addDoc(collection(db, 'projects', id, 'tasks', taskId, 'quotes'), {
          supplierId: supplierId,
          supplierName: supplier.name,
          supplierEmail: supplier.email,
          status: 'invited',
          magicLink: magicLink,
          message: quoteMessage,
          invitedAt: new Date(),
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
        });

        // Create quote request in main collection for Firebase Functions
        await addDoc(collection(db, 'quoteRequests'), {
          projectId: id,
          projectName: project.name,  // Now we know project exists from check above
          taskId: taskId,
          taskName: task.name,  // Now we know task exists from check above
          taskDescription: task.description || '',
          taskCategory: task.category || 'general',
          supplierId: supplier.id,
          supplierName: supplier.name,
          supplierEmail: supplier.email,
          token: token,
          status: 'pending',
          invitedAt: serverTimestamp(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          estimatedDays: task?.estimatedDays || 7,
          budget: task?.budget || 0,
          quoteAmount: null,
          quotedAt: null,
          notes: quoteMessage,
          // Include product specifications for supplier portal
          productSpecs: {
            product: task.product || '',
            category: task.category || '', 
            subcategory: task.subcategory || '',
            dimensions: task.dimensions || {},
            materials: task.materials || [],
            features: task.features || [],
            additionals: task.additionals || [],
            fabric: task.fabric || null,
            componentName: task.componentName || '',
            componentNotes: task.componentNotes || ''
          },
          projectDescription: project.description || '',
          // Include photos for supplier portal
          photos: {
            general: generalPhotos,
            component: componentPhotos,
            fabric: fabricPhotos
          }
        });
        
        // Create communication record for tracking
        await addDoc(collection(db, 'communications'), {
          projectId: id,
          projectName: project.name,
          subject: `Quote Invitation: ${task.name}`,
          message: quoteMessage || `You've been invited to provide a quote for: ${task.name}`,
          senderType: 'pfi',
          senderId: 'system',
          senderName: 'PFI Connect',
          recipientTypes: ['supplier'],
          recipientIds: [supplier.id],
          recipientNames: [supplier.name],
          status: 'sent',
          createdAt: serverTimestamp(),
          relatedTo: {
            type: 'quote-invitation',
            referenceId: taskId,
            taskName: task.name,
            magicLink: magicLink
          }
        });

        // Create notification to trigger email and SMS
        await addDoc(collection(db, 'notifications'), {
          type: 'quote-invitation-sent',
          recipientId: supplier.id,
          recipientEmail: supplier.email,
          recipientPhone: supplier.phone || null,
          recipientName: supplier.name,
          projectId: id,
          projectName: project.name,  // Now we know project exists
          taskId: taskId,
          taskName: task.name,  // Now we know task exists
          taskDescription: task.description || '',
          taskCategory: task.category || 'general',
          estimatedDays: task.estimatedDays || 7,
          budget: task.budget || 0,
          message: `You've been invited to quote on: ${task.name}`,
          magicLink: magicLink,
          status: 'pending',
          createdAt: serverTimestamp()
        });
        
        console.log(`✅ Notification created for ${supplier.email} with link: ${magicLink}`);
      }
      
      setSelectedSuppliers([]);
      setQuoteMessage('');
      setShowInviteForm(false);
      loadData();
      
      alert(`✅ Invitations sent to ${selectedSuppliers.length} supplier(s)!\n\nThey will receive email and SMS notifications with magic links.`);
    } catch (error) {
      console.error('Error inviting suppliers:', error);
      alert('Failed to send invitations: ' + error.message);
    }
  };

  const handleQuoteAction = async (quoteId, action) => {
    try {
      // Update the quote status
      await updateDoc(doc(db, 'projects', id, 'tasks', taskId, 'quotes', quoteId), {
        status: action,
        actionedAt: serverTimestamp()
      });

      // Update task status based on action
      let newTaskStatus = task.status;
      if (action === 'accepted') {
        newTaskStatus = 'quote_accepted';
        
        // Create notification for quote acceptance
        await addDoc(collection(db, 'notifications'), {
          type: 'quote_accepted',
          title: 'Quote Accepted',
          message: `Quote accepted for task "${task.name}" in project "${project.name}"`,
          projectId: id,
          taskId: taskId,
          quoteId: quoteId,
          read: false,
          createdAt: serverTimestamp(),
          recipientRole: 'admin' // Notify PFI admins
        });
      } else if (action === 'declined') {
        // Check if there are any other submitted quotes
        const remainingQuotes = quotes.filter(q => q.id !== quoteId && q.status === 'submitted');
        newTaskStatus = remainingQuotes.length > 0 ? 'quotes_received' : 'quotes_pending';
      }

      // Update task status
      if (newTaskStatus !== task.status) {
        await updateDoc(doc(db, 'projects', id, 'tasks', taskId), {
          status: newTaskStatus,
          statusUpdatedAt: serverTimestamp()
        });
      }

      loadData();
    } catch (error) {
      console.error('Error updating quote:', error);
    }
  };

  const handleDeleteQuote = async (quoteId, supplierName) => {
    if (window.confirm(`Are you sure you want to delete the quote invitation for ${supplierName}?`)) {
      try {
        // Find the quote in our current data to determine which collection it's from
        const quote = quotes.find(q => q.id === quoteId);
        
        if (!quote) {
          alert('Quote not found');
          return;
        }
        
        console.log('Deleting quote:', quote.id, 'from collection:', quote._collection, 'supplier:', quote.supplierId);
        
        // 1. Delete from quote collections
        if (quote._collection === 'oldQuotes') {
          // Delete from old structure
          const oldStructureDoc = doc(db, 'projects', id, 'tasks', taskId, 'quotes', quoteId);
          await deleteDoc(oldStructureDoc);
        } else if (quote._collection === 'quoteRequests') {
          // Delete from new structure
          const quoteRequestDoc = doc(db, 'quoteRequests', quoteId);
          await deleteDoc(quoteRequestDoc);
        } else {
          // Fallback - try both collections
          console.log('Unknown collection, trying both...');
          try {
            await deleteDoc(doc(db, 'quoteRequests', quoteId));
          } catch (e) {
            await deleteDoc(doc(db, 'projects', id, 'tasks', taskId, 'quotes', quoteId));
          }
        }
        
        // 2. Remove supplier from task's invitedSuppliers array
        if (task && quote.supplierId) {
          const taskRef = doc(db, 'projects', id, 'tasks', taskId);
          const taskDoc = await getDoc(taskRef);
          
          if (taskDoc.exists()) {
            const taskData = taskDoc.data();
            const invitedSuppliers = taskData.invitedSuppliers || [];
            
            // Remove the supplier from the invitedSuppliers array
            const updatedSuppliers = invitedSuppliers.filter(supplier => supplier.id !== quote.supplierId);
            
            console.log('Removing supplier from task invitedSuppliers:', quote.supplierId);
            console.log('Before:', invitedSuppliers.length, 'After:', updatedSuppliers.length);
            
            await updateDoc(taskRef, {
              invitedSuppliers: updatedSuppliers,
              updatedAt: new Date()
            });
          }
        }
        
        // Reload data to refresh the display
        await loadData();
        alert('Quote invitation deleted successfully');
      } catch (error) {
        console.error('Error deleting quote:', error);
        alert('Failed to delete quote: ' + error.message);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-gradient-to-r from-green-400 to-emerald-500';
      case 'submitted':
        return 'bg-gradient-to-r from-blue-400 to-indigo-500';
      case 'invited':
        return 'bg-gradient-to-r from-yellow-400 to-orange-500';
      case 'declined':
        return 'bg-gradient-to-r from-red-400 to-pink-500';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading quotes...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Link
              to={`/projects/${id}/tasks`}
              className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Tasks
            </Link>
            <button
              onClick={() => setShowInviteForm(true)}
              className="inline-flex items-center px-6 py-3 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors shadow-lg"
            >
              <UserGroupIcon className="w-5 h-5 mr-2" />
              Invite Suppliers
            </button>
          </div>
          
          <div className="flex items-start">
            <EnvelopeIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Quote Requests</h1>
              <p className="text-pink-100 text-xl">
                {task?.name} - {formatProjectName(project)}
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <EnvelopeIcon className="w-5 h-5 mr-2" />
                  {quotes.length} Quotes Requested
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  {quotes.filter(q => q.status === 'submitted').length} Submitted
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2" />
                  {quotes.filter(q => q.status === 'invited').length} Pending
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Invite Suppliers Modal */}
        {showInviteForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Invite Suppliers to Quote</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Select Suppliers</label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                      {suppliers.map(supplier => (
                        <label key={supplier.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(supplier.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSuppliers([...selectedSuppliers, supplier.id]);
                              } else {
                                setSelectedSuppliers(selectedSuppliers.filter(id => id !== supplier.id));
                              }
                            }}
                            className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{supplier.name}</div>
                            <div className="text-sm text-gray-500">{supplier.email}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message to Suppliers</label>
                    <textarea
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={quoteMessage}
                      onChange={(e) => setQuoteMessage(e.target.value)}
                      placeholder="Please provide your quote for this task. Include timeline, pricing, and any relevant details..."
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <LinkIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Magic Link Authentication</p>
                        <p>Suppliers will receive a unique link that allows them to submit quotes without creating an account.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <button
                      onClick={handleInviteSuppliers}
                      disabled={selectedSuppliers.length === 0}
                      className="px-8 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Send Invitations ({selectedSuppliers.length})
                    </button>
                    <button
                      onClick={() => setShowInviteForm(false)}
                      className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quotes Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6 pfi-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(quote.status)}`}>
                      {quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{quote.supplierName}</h3>
                  <p className="text-gray-600 text-sm">{quote.supplierEmail}</p>
                </div>
                
                <div className="flex items-center space-x-2">
                  {quote.magicLink && (
                    <button
                      onClick={() => navigator.clipboard.writeText(quote.magicLink)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Copy magic link"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button className="p-2 text-gray-400 hover:text-green-600 transition-colors">
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteQuote(quote.id, quote.supplierName)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete quote invitation"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {quote.message && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-700 text-sm">{quote.message}</p>
                </div>
              )}

              {(quote.amount || quote.quoteAmount) && (
                <div className="mb-4">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(quote.amount || quote.quoteAmount)}
                  </div>
                  {quote.timeline && (
                    <div className="text-sm text-gray-500">
                      Timeline: {quote.timeline}
                    </div>
                  )}
                </div>
              )}

              {quote.description && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-1">Quote Description</h4>
                  <p className="text-gray-700 text-sm">{quote.description}</p>
                </div>
              )}

              {quote.materials && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-1">Materials & Specifications</h4>
                  <p className="text-gray-700 text-sm">{quote.materials}</p>
                </div>
              )}

              {quote.notes && (
                <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-1">Additional Notes</h4>
                  <p className="text-gray-700 text-sm">{quote.notes}</p>
                </div>
              )}

              <div className="space-y-2 mb-4 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span>Invited:</span>
                  <span>{formatDate(quote.invitedAt)}</span>
                </div>
                {quote.dueDate && (
                  <div className="flex items-center justify-between">
                    <span>Due:</span>
                    <span>{formatDate(quote.dueDate)}</span>
                  </div>
                )}
                {quote.submittedAt && (
                  <div className="flex items-center justify-between">
                    <span>Submitted:</span>
                    <span>{formatDate(quote.submittedAt)}</span>
                  </div>
                )}
              </div>

              {quote.status === 'submitted' && (
                <div className="flex items-center space-x-3 pt-4 border-t">
                  <button
                    onClick={() => handleQuoteAction(quote.id, 'accepted')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleQuoteAction(quote.id, 'declined')}
                    className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircleIcon className="w-4 h-4 inline mr-1" />
                    Decline
                  </button>
                  <Link
                    to={`/projects/${id}/tasks/${taskId}/quotes/compare`}
                    className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Compare
                  </Link>
                </div>
              )}

              {quote.status === 'invited' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    Waiting for supplier response
                  </div>
                </div>
              )}

              {quote.status === 'accepted' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center text-sm text-green-600 font-semibold">
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Quote Accepted - Supplier Appointed
                  </div>
                </div>
              )}
            </div>
          ))}

          {quotes.length === 0 && (
            <div className="col-span-full text-center py-12">
              <EnvelopeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes requested yet</h3>
              <p className="text-gray-500 mb-6">Invite suppliers to submit quotes for this task</p>
              <button
                onClick={() => setShowInviteForm(true)}
                className="inline-flex items-center px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
              >
                <UserGroupIcon className="w-5 h-5 mr-2" />
                Invite Suppliers
              </button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}